import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from './language-context';
import { SignOutLabel, useSigningOut } from './sign-out-progress';
import { useLayer, LayerScrim } from './layer-stack';
import { Icon } from './icon';
import { moreItems } from './nav-items';
import type { ShellBadges } from './nav-items';
import type { User } from '../mock/seeds/schema-types';
import { roleSwitchLinks } from './role-switch-links';

// The mobile More sheet (CB-17; Others.dc.html L292-318): Account and Help as in the
// design, plus Messages and Claims — kept by DR-38 "under More" — then the other
// dashboards this account can open (role-switch-links.ts), then Sign out. A
// bottom sheet on the layer stack: Esc / browser Back / the scrim close it, focus
// returns to the More tab. Styles: `.more-sheet*` in tab-bar.css.
export function MoreSheet({
  open,
  onClose,
  badges,
  user,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  badges: ShellBadges;
  user?: User | undefined;
  onSignOut: () => void;
}) {
  const { t, lang } = useLanguage();
  const signingOut = useSigningOut();
  const { z } = useLayer(open, onClose, { kind: 'sheet' });
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <LayerScrim z={z} onClose={onClose} className="sheet-backdrop more-sheet-backdrop">
      <div
        ref={panelRef}
        className="more-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('user.shell.more.title')}
        tabIndex={-1}
        data-motion=""
      >
        <div className="more-sheet__grab" aria-hidden="true" />
        <div className="more-sheet__head">
          <div className="more-sheet__titles">
            <span className="more-sheet__title">{t('user.shell.more.title')}</span>
            <span className="more-sheet__sub">{t('user.shell.more.sub')}</span>
          </div>
          <button type="button" className="more-sheet__close" aria-label={t('action.close')} onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="more-sheet__items">
          {moreItems.map((item) => {
            const count = item.badge ? badges[item.badge] : 0;
            return (
              <Link key={item.id} to={item.to} className="more-sheet__item" onClick={onClose}>
                <span className="more-sheet__icon">
                  <Icon name={item.icon} size={17} />
                </span>
                <span className="more-sheet__label">{t(item.key)}</span>
                {count > 0 && <span className="more-sheet__count">{formatNumber(lang, count)}</span>}
                <span className="more-sheet__chev">
                  <Icon name="chevronRight" size={16} />
                </span>
              </Link>
            );
          })}
          {roleSwitchLinks(user).map((link) => (
            <a key={link.id} href={link.href} className="more-sheet__item">
              <span className="more-sheet__icon">
                <Icon name={link.icon} size={17} />
              </span>
              <span className="more-sheet__label">{t(link.key)}</span>
              <span className="more-sheet__chev">
                <Icon name="chevronRight" size={16} />
              </span>
            </a>
          ))}
          <button type="button" className="more-sheet__signout" onClick={onSignOut} disabled={signingOut} aria-busy={signingOut}>
            <SignOutLabel label={t('user.shell.sign_out')} />
          </button>
        </div>
      </div>
    </LayerScrim>
  );
}
